import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Save, Wand2, Check, X, ArrowRight, RotateCcw, Star, ExternalLink, Plus, Globe, Route } from "lucide-react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getStoreId } from "../utils/auth";
import * as logger from "../utils/logger";
import { showToast } from "../utils/toast";
import "./SEOOptimizerDetail.css";
import SeoFixDetailGuidedTour, { hasCompletedSeoFixDetailTour } from "./SeoFixDetailGuidedTour";
import SeoFixDetailTourReplayHintFlyout from "./SeoFixDetailTourReplayHintFlyout";

const violetAiBtnStyle = {
  background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
  color: "#fff",
  border: "none",
};
const skyRankBtnStyle = {
  background: "#e0f2fe",
  borderColor: "#38bdf8",
  color: "#0369a1",
};

/** Normalize description for ReactQuill: fix literal \\n, markdown-style bold (** and __), and plain-text newlines/bullets so they render correctly. */
function normalizeDescriptionForQuill(value) {
  if (value == null || value === '') return '';
  let s = String(value);
  // Replace literal backslash-n (single and double-escaped from API/DB) with real newline
  s = s.replace(/\\\\n/g, '\n').replace(/\\n/g, '\n').replace(/\\\\r/g, '\r').replace(/\\r/g, '\r');
  // Convert markdown-style bold to HTML (so **text** and __text__ render as bold, not literal asterisks)
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>').replace(/__([^_]+)__/g, '<strong>$1</strong>');
  const withNewlines = s;
  // If it looks like HTML, normalize newlines and convert bullet paragraphs to lists
  if (/<[a-z][\s\S]*>/i.test(withNewlines)) {
    let html = withNewlines.replace(/\r\n/g, '\n').replace(/\n/g, '<br/>');
    // Convert <p>• ...</p> (or <p><br/>• ...</p>) sequences to <ul><li>...</li></ul>
    html = html.replace(/(<p[^>]*>)\s*<br\/>\s*[•·\-*]\s*([^<]*)<\/p>/gi, '<li>$2</li>');
    html = html.replace(/(<p[^>]*>)\s*[•·\-*]\s*([^<]*)<\/p>/gi, '<li>$2</li>');
    html = html.replace(/(<li>.*?<\/li>\s*)+/gs, (match) => '<ul>' + match.trim() + '</ul>');
    return html;
  }
  // Plain text: turn newlines into paragraphs; convert bullet lines into <ul><li>
  const lines = withNewlines.split(/\n/);
  const out = [];
  let i = 0;
  const escape = (line) => line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const isBullet = (line) => /^[\s]*[•·\-*]\s/.test(line) || /^[\s]*\d+\.\s/.test(line);
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === '') {
      out.push('<p><br></p>');
      i++;
      continue;
    }
    if (isBullet(line)) {
      const listItems = [];
      while (i < lines.length && (isBullet(lines[i]) || lines[i].trim() === '')) {
        if (lines[i].trim() !== '') {
          const trimmed = lines[i].replace(/^[\s]*[•·\-*]\s*/, '').replace(/^[\s]*\d+\.\s*/, '');
          // Trimmed may contain <strong> from earlier conversion - don't escape it
          const content = trimmed.includes('<strong>') ? trimmed : escape(trimmed);
          listItems.push(`<li>${content}</li>`);
        }
        i++;
      }
      if (listItems.length) out.push(`<ul>${listItems.join('')}</ul>`);
      continue;
    }
    // Line may contain <strong> from ** conversion - don't escape so it renders as bold
    const content = line.includes('<strong>') ? line : escape(line);
    out.push(`<p>${content}</p>`);
    i++;
  }
  return out.join('');
}

const CONTENT_LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English' }, { code: 'es', label: 'Spanish' }, { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' }, { code: 'it', label: 'Italian' }, { code: 'pt', label: 'Portuguese' },
  { code: 'nl', label: 'Dutch' }, { code: 'pl', label: 'Polish' }, { code: 'ru', label: 'Russian' },
  { code: 'ja', label: 'Japanese' }, { code: 'zh', label: 'Chinese' }, { code: 'ko', label: 'Korean' },
  { code: 'ar', label: 'Arabic' }, { code: 'hi', label: 'Hindi' }, { code: 'tr', label: 'Turkish' },
  { code: 'vi', label: 'Vietnamese' }, { code: 'th', label: 'Thai' }, { code: 'id', label: 'Indonesian' },
  { code: 'sv', label: 'Swedish' }, { code: 'da', label: 'Danish' }, { code: 'no', label: 'Norwegian' },
  { code: 'fi', label: 'Finnish' }, { code: 'el', label: 'Greek' }, { code: 'he', label: 'Hebrew' },
  { code: 'ro', label: 'Romanian' }, { code: 'hu', label: 'Hungarian' }, { code: 'cs', label: 'Czech' },
  { code: 'sk', label: 'Slovak' }, { code: 'bg', label: 'Bulgarian' }, { code: 'hr', label: 'Croatian' }
];

const SEOOptimizerDetail = ({ page, pages = [], currentIndex = 0, onSelectPage, onClose, onSave }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAction, setSavingAction] = useState(null); // 'save' | 'saveAndExit' | null
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshingImages, setRefreshingImages] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [detailTourRun, setDetailTourRun] = useState(false);
  const [detailTourReplayFlyout, setDetailTourReplayFlyout] = useState(false);
  
  // Form fields
  const [name, setName] = useState('');
  const [titleTag, setTitleTag] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [description, setDescription] = useState('');
  const [targetKeyword, setTargetKeyword] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [primaryImageAltText, setPrimaryImageAltText] = useState('');
  const [allImages, setAllImages] = useState([]);
  // Category-only: parent name, product count (populated from detail API)
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [detailAdditionalData, setDetailAdditionalData] = useState(null); // from detail API (product: color, dimensions, brand, etc.)
  const [contentLanguage, setContentLanguage] = useState('en'); // from site_settings.seo_settings.content_language
  /** When false, detail save and bulk SEO jobs skip pushing that slice to Ecwid (stored in site_settings.seo_settings). */
  const [sectionPublishToEcwid, setSectionPublishToEcwid] = useState({
    targetKeyword: true,
    meta: true,
    content: true,
    images: true,
    url: true,
  });
  
  // SEO Scores
  const [seoScore, setSeoScore] = useState(0);
  const [seoScores, setSeoScores] = useState({
    meta: 0,
    content: 0,
    image: 0,
    broken: 0,
    url: 0
  });
  
  // AI Generation
  const [aiLoading, setAiLoading] = useState({
    title: false,
    meta_description: false,
    description: false,
    keywords: false,
    alt_text_all: false
  });
  const [aiLoadingImages, setAiLoadingImages] = useState({}); // Track loading per image ID
  
  // Initial values for change detection
  const [initialValues, setInitialValues] = useState(null);
  
  // Link validation state
  const [linkValidation, setLinkValidation] = useState({
    httpLinks: 0,
    internalBrokenLinks: 0,
    externalBrokenLinks: 0,
    isValidating: false
  });
  
  // Add Keyword to Rank Tracker modal
  const [showAddKeywordModal, setShowAddKeywordModal] = useState(false);
  const [addKeywordKeyword, setAddKeywordKeyword] = useState('');
  const [addKeywordSelectedKeywords, setAddKeywordSelectedKeywords] = useState([]); // multi-select when dropdown is shown
  const [addKeywordCountry, setAddKeywordCountry] = useState('United States');
  const [addKeywordDevice, setAddKeywordDevice] = useState('Desktop');
  const [addKeywordEngine, setAddKeywordEngine] = useState('google.com');
  const [addKeywordLanguage, setAddKeywordLanguage] = useState('English');
  const [addKeywordSubmitting, setAddKeywordSubmitting] = useState(false);
  
  const descriptionWrapperRef = useRef(null);
  const aiFixAnchorRef = useRef(null);
  const addKeywordSubmitRef = useRef(null);
  const saveButtonRef = useRef(null);
  const contentLanguageRef = useRef(null);
  const altTextAllButtonRef = useRef(null);
  // Store initial SEO data from API for duplicate checking
  const [initialSeoData, setInitialSeoData] = useState({
    duplicateTitleTags: 0,
    duplicateMetaDescriptions: 0,
    httpLinks: 0,
    internalBrokenLinks: 0,
    externalBrokenLinks: 0,
    spellingErrors: 0
  });
  
  const apiUrl = import.meta.env.VITE_BACKEND_URL;
  const hashSiteId = getStoreId();

  const updateSectionPublishToEcwidKey = useCallback(async (key, value) => {
    let next;
    setSectionPublishToEcwid((prev) => {
      next = { ...prev, [key]: value };
      return next;
    });
    if (!hashSiteId || !apiUrl) return;
    try {
      const res = await fetch(`${apiUrl}/api/seo-optimizer/settings?id=${hashSiteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section_publish_to_ecwid: next }),
      });
      if (!res.ok) throw new Error('Failed to save section settings');
    } catch (err) {
      logger.error('section_publish_to_ecwid save:', err);
      showToast(t('seoOptimizerDetail.failedToSaveSectionPublish'), 'error', 2500);
      setSectionPublishToEcwid((prev) => ({ ...prev, [key]: !value }));
    }
  }, [hashSiteId, apiUrl, t]);
  
  const totalRecords = pages.length || 1;
  const currentPosition = currentIndex >= 0 ? currentIndex + 1 : 1;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < totalRecords - 1;
  
  // Check for unsaved changes
  const hasUnsavedChanges = () => {
    if (!initialValues) return false;
    
    if (titleTag !== initialValues.titleTag) return true;
    if (metaDescription !== initialValues.metaDescription) return true;
    if (description !== initialValues.description) return true;
    if (customUrl !== initialValues.customUrl) return true;
    if (targetKeyword !== initialValues.targetKeyword) return true;
    
    // Check image alt texts
    if (allImages.length !== initialValues.allImages.length) return true;
    for (const img of allImages) {
      const initialImg = initialValues.allImages.find(i => i.id === img.id);
      if (!initialImg || img.alt_text !== initialImg.alt_text) return true;
    }
    
    return false;
  };
  
  const handleNavigation = (navigationFn) => {
    if (hasUnsavedChanges()) {
      setPendingNavigation(() => navigationFn);
      setShowUnsavedChangesModal(true);
    } else {
      navigationFn();
    }
  };
  
  const handlePrev = () => {
    if (hasPrev) {
      handleNavigation(() => onSelectPage(pages[currentIndex - 1]));
    }
  };
  
  const handleNext = () => {
    if (hasNext) {
      handleNavigation(() => onSelectPage(pages[currentIndex + 1]));
    }
  };
  
  const handleClose = () => {
    handleNavigation(() => onClose());
  };
  
  // Fetch page details
  useEffect(() => {
    if (!page || !hashSiteId) return;
    
    const fetchDetail = async () => {
      setLoading(true);
      setAllImages([]); // clear previous product's images so badge doesn't show stale count
      setDetailAdditionalData(null); // clear until detail returns additional_data for products
      try {
        const response = await fetch(
          `${apiUrl}/api/seo-optimizer/detail?id=${hashSiteId}&pageId=${page.pageId || page.id}&pageType=${page.pageType || page.type || 'product'}`
        );
        
        if (response.ok) {
          const data = await response.json();
          
          setName(data.name || data.pageName || '');
          setTitleTag(data.title_tag || data.meta_title || '');
          setMetaDescription(data.meta_description || '');
          setDescription(normalizeDescriptionForQuill(data.description || ''));
          setTargetKeyword(data.target_keyword || data.name || '');
          setCustomUrl(data.custom_url || data.url_link || '');
          setPrimaryImageAltText(data.primary_image_alt_text || '');
          // Category-only: parent and product count (from detail API)
          if (data.type === 'category') {
            setCategoryInfo({
              parentCategoryName: data.parentCategoryName ?? null,
              productCount: data.productCount ?? null,
              enabled: data.enabled
            });
          } else {
            setCategoryInfo(null);
          }
          // Handle both all_images and images fields; normalize so each image has alt_text as string for correct badge count
          const imagesData = data.all_images || data.images || [];
          const rawList = Array.isArray(imagesData) ? imagesData : [];
          const normalizedImages = rawList.map((img, idx) => {
            const altRaw = img.alt_text ?? img.alt ?? img.altText ?? (img.alt && typeof img.alt === 'object' && img.alt != null && 'main' in img.alt ? img.alt.main : null) ?? img.description ?? '';
            const altStr = typeof altRaw === 'string' ? altRaw.trim() : (altRaw != null && typeof altRaw === 'object' && 'main' in altRaw ? String(altRaw.main ?? '').trim() : '');
            return {
              ...img,
              id: img.id ?? img.imageId ?? String(idx),
              alt_text: altStr,
              url: img.url ?? img.imageUrl ?? img.originalUrl,
              thumbnail_url: img.thumbnail_url ?? img.thumbnailUrl ?? img.url ?? img.imageUrl,
              is_primary: img.is_primary ?? img.isPrimary ?? (idx === 0 && !rawList.some((i) => i.isMain || i.is_primary))
            };
          });
          setAllImages(normalizedImages);
          setSeoScore(data.seo_score || 0);
          setSeoScores({
            meta: data.meta_issues || data.meta_score || 0,
            content: data.content_issues || data.content_score || 0,
            image: data.image_issues || data.image_score || 0,
            broken: data.broken_issues || data.broken_score || 0,
            url: data.url_issues || data.url_score || 0
          });
          
          // Store initial SEO data
          setInitialSeoData({
            duplicateTitleTags: data.duplicate_title_tags || 0,
            duplicateMetaDescriptions: data.duplicate_meta_descriptions || 0,
            httpLinks: data.http_links || 0,
            internalBrokenLinks: data.internal_broken_links || 0,
            externalBrokenLinks: data.external_broken_links || 0,
            spellingErrors: data.spelling_errors || 0
          });
          
          // Store initial values (description normalized for Quill so change detection matches)
          const normalizedDesc = normalizeDescriptionForQuill(data.description || '');
          setInitialValues({
            titleTag: data.title_tag || data.meta_title || '',
            metaDescription: data.meta_description || '',
            description: normalizedDesc,
            customUrl: data.custom_url || '',
            targetKeyword: data.target_keyword || '',
            allImages: (data.all_images || data.images || []).map(img => ({ id: img.id, alt_text: img.alt_text || '' }))
          });
          // Product detail: additional_data from API (color, dimensions, brand, name, search_keywords)
          setDetailAdditionalData(data.type === 'product' && data.additional_data != null ? data.additional_data : null);
        }
      } catch (error) {
        logger.error('Error fetching detail:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDetail();
  }, [page, hashSiteId, apiUrl, refreshKey]);

  // Fetch SEO Optimizer settings (content language) when we have hashSiteId
  useEffect(() => {
    if (!hashSiteId || !apiUrl) return;
    const fetchSeoSettings = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/seo-optimizer/settings?id=${hashSiteId}`);
        if (res.ok) {
          const data = await res.json();
          const lang = data.seo_settings?.content_language;
          if (lang != null && String(lang).trim() !== '') setContentLanguage(String(lang).trim());
          const sp = data.seo_settings?.section_publish_to_ecwid;
          if (sp && typeof sp === 'object') {
            setSectionPublishToEcwid((prev) => ({ ...prev, ...sp }));
          }
        }
      } catch (e) {
        logger.error('Error fetching SEO settings:', e);
      }
    };
    fetchSeoSettings();
  }, [hashSiteId, apiUrl]);

  useEffect(() => {
    if (loading) return;
    if (hasCompletedSeoFixDetailTour()) return;
    const timer = window.setTimeout(() => setDetailTourRun(true), 1100);
    return () => clearTimeout(timer);
  }, [loading]);
  
  // Validate links in description
  useEffect(() => {
    const validateLinks = async () => {
      if (!description || !description.trim()) {
        setLinkValidation({
          httpLinks: 0,
          internalBrokenLinks: 0,
          externalBrokenLinks: 0,
          isValidating: false
        });
        return;
      }

      setLinkValidation(prev => ({ ...prev, isValidating: true }));

      // Extract all links from HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(description, 'text/html');
      const links = Array.from(doc.querySelectorAll('a[href]'));
      
      let httpCount = 0;
      let internalBrokenCount = 0;
      let externalBrokenCount = 0;

      // Get current domain for internal link detection
      const currentDomain = window.location.hostname;

      for (const link of links) {
        const href = link.getAttribute('href');
        if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
          continue;
        }

        try {
          const url = new URL(href, window.location.origin);
          
          // Check for HTTP (non-HTTPS)
          if (url.protocol === 'http:') {
            httpCount++;
          }

          // Check if link is internal or external
          const isInternal = url.hostname === currentDomain || url.hostname === '';

          // Validate link (HEAD request)
          try {
            const response = await fetch(url.toString(), { 
              method: 'HEAD',
              mode: 'no-cors' // Avoid CORS issues for external links
            });
            
            // For no-cors mode, we can't check status, so we assume it's valid if it doesn't throw
            if (response.type !== 'opaque' && !response.ok) {
              if (isInternal) {
                internalBrokenCount++;
              } else {
                externalBrokenCount++;
              }
            }
          } catch (error) {
            // Link is broken
            if (isInternal) {
              internalBrokenCount++;
            } else {
              externalBrokenCount++;
            }
          }
        } catch (error) {
          // Invalid URL format
          logger.error('Invalid URL:', href);
        }
      }

      setLinkValidation({
        httpLinks: httpCount,
        internalBrokenLinks: internalBrokenCount,
        externalBrokenLinks: externalBrokenCount,
        isValidating: false
      });
    };

    // Debounce validation to avoid too many requests
    const timeoutId = setTimeout(validateLinks, 1000);
    return () => clearTimeout(timeoutId);
  }, [description]);

  // When pasted content looks like HTML, parse it so it renders as rich content instead of showing raw tags
  useEffect(() => {
    const id = setTimeout(() => {
      const wrapper = descriptionWrapperRef.current;
      if (!wrapper) return;
      const container = wrapper.querySelector('.ql-container');
      if (!container) return;
      Promise.all([import('quill'), import('react-quill')]).then(([quillMod, rqMod]) => {
        const Quill = quillMod?.default ?? quillMod?.Quill ?? rqMod?.Quill;
        if (!Quill?.find) return;
        const quill = Quill.find(container);
        if (!quill) return;
        const clipboard = quill.getModule('clipboard');
        if (!clipboard) return;
        clipboard.addMatcher(Node.TEXT_NODE, (_node, delta) => {
          const op = delta.ops?.[0];
          const insert = op?.insert;
          if (typeof insert === 'string' && insert.includes('<') && insert.includes('>')) {
            return clipboard.convert(insert);
          }
          return delta;
        });
      }).catch(() => { /* ignore clipboard matcher failure */ });
    }, 100);
    return () => clearTimeout(id);
  }, [page?.id]);

  // Calculate SEO checklist items — use target keyword when available, else product/category name
  const effectiveKeyword = (targetKeyword && String(targetKeyword).trim()) ? String(targetKeyword).trim() : (name || '').trim();
  const effectiveKeywordStr = effectiveKeyword ? String(effectiveKeyword) : '';
  // Comma/newline-separated keywords: each phrase checked independently for "keyword in title/meta/description/URL"
  const effectiveKeywordList = effectiveKeywordStr ? effectiveKeywordStr.split(/[,\n]/).map(s => s.trim()).filter(s => s.length > 0) : [];
  const titleTagStr = titleTag ? String(titleTag) : '';
  const metaDescriptionStr = metaDescription ? String(metaDescription) : '';
  const descriptionStr = description ? String(description) : '';
  
  const titleCharCount = titleTagStr.length;
  const metaDescCharCount = metaDescriptionStr.length;
  const descriptionPlainText = descriptionStr.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  const descriptionCharCount = descriptionPlainText.length;
  const descriptionWordCount = descriptionPlainText ? descriptionPlainText.split(/\s+/).filter(word => word.length > 0).length : 0;
  const descriptionTextForKeywordCheck = descriptionPlainText.toLowerCase();
  
  const normalizedCustomUrl = customUrl ? customUrl.trim() : '';
  let customUrlPath = normalizedCustomUrl;
  try {
    const urlObj = new URL(normalizedCustomUrl.startsWith('http') ? normalizedCustomUrl : `https://example.com${normalizedCustomUrl}`);
    customUrlPath = urlObj.pathname;
  } catch (e) {
    customUrlPath = normalizedCustomUrl;
  }
  const customUrlPathNormalized = customUrlPath.replace(/^\/+|\/+$/g, '');
  const customUrlKeywordHaystack = customUrlPathNormalized.toLowerCase().replace(/[^a-z0-9-]/g, '');

  // Words to check for "Target Keyword present in URL": from both target keyword and product/category name (either passes)
  const urlCheckWords = useMemo(() => {
    const words = new Set();
    effectiveKeywordList.forEach(phrase => {
      phrase.toLowerCase().split(/[\s-]+/).filter(w => w.length >= 2).forEach(w => {
        const c = w.replace(/[^a-z0-9]/g, '');
        if (c) words.add(c);
      });
    });
    (name || '').trim().toLowerCase().split(/[\s-]+/).filter(w => w.length >= 2).forEach(w => {
      const c = w.replace(/[^a-z0-9]/g, '');
      if (c) words.add(c);
    });
    return [...words];
  }, [effectiveKeywordList, name]);
  const keywordInUrl = customUrlKeywordHaystack && urlCheckWords.length > 0 && urlCheckWords.some(word => customUrlKeywordHaystack.includes(word));
  
  // Meta Tag Issues Checklist
  const metaTagIssues = useMemo(() => [
    { 
      label: t('seoOptimizerDetail.checklistTitleTagLength'), 
      passed: titleCharCount >= 40 && titleCharCount <= 60, 
      value: titleCharCount.toString() 
    },
    { 
      label: t('seoOptimizerDetail.checklistMetaDescLength'), 
      passed: metaDescCharCount >= 120 && metaDescCharCount <= 160, 
      value: metaDescCharCount.toString() 
    },
    { 
      label: t('seoOptimizerDetail.checklistKeywordInTitle'), 
      passed: titleTagStr && effectiveKeywordList.length > 0 && effectiveKeywordList.some(kw => titleTagStr.toLowerCase().includes(kw.toLowerCase())) 
    },
    { 
      label: t('seoOptimizerDetail.checklistKeywordInMeta'), 
      passed: metaDescriptionStr && effectiveKeywordList.length > 0 && effectiveKeywordList.some(kw => metaDescriptionStr.toLowerCase().includes(kw.toLowerCase())) 
    },
    { 
      label: t('seoOptimizerDetail.checklistUniqueTitleTag'), 
      passed: (initialSeoData?.duplicateTitleTags || 0) === 0,
      count: initialSeoData?.duplicateTitleTags || 0
    },
    { 
      label: t('seoOptimizerDetail.checklistUniqueMetaDesc'), 
      passed: (initialSeoData?.duplicateMetaDescriptions || 0) === 0,
      count: initialSeoData?.duplicateMetaDescriptions || 0
    },
  ], [t, titleCharCount, metaDescCharCount, titleTagStr, effectiveKeywordList, metaDescriptionStr, initialSeoData]);
  
  // Content Issues Checklist
  const contentIssues = [
    { 
      label: t('seoOptimizerDetail.checklistKeywordInDesc'), 
      passed: effectiveKeywordList.length > 0 && descriptionStr && descriptionStr.trim() && 
        effectiveKeywordList.some(kw => String(descriptionStr).replace(/<[^>]*>/g, '').toLowerCase().includes(kw.toLowerCase()))
    },
    { 
      label: t('seoOptimizerDetail.checklistLoremIpsum'), 
      passed: !descriptionTextForKeywordCheck.includes('lorem ipsum') 
    },
    { 
      label: t('seoOptimizerDetail.checklistNoInternalBroken', { count: linkValidation.internalBrokenLinks }), 
      passed: linkValidation.internalBrokenLinks === 0,
      count: linkValidation.internalBrokenLinks 
    },
    { 
      label: t('seoOptimizerDetail.checklistNoExternalBroken', { count: linkValidation.externalBrokenLinks }),
      passed: linkValidation.externalBrokenLinks === 0,
      count: linkValidation.externalBrokenLinks 
    },
    { 
      label: t('seoOptimizerDetail.checklistNoHttpLinks', { count: linkValidation.httpLinks }), 
      passed: linkValidation.httpLinks === 0,
      count: linkValidation.httpLinks 
    },
    { 
      label: t('seoOptimizerDetail.checklistSpellingErrors'), 
      passed: true,
      count: 0 
    },
  ];
  
  // Normalize alt (string or Ecwid object { main: "..." }) so we count missing alt correctly
  const getAltStr = (val) => {
    if (val == null) return '';
    if (typeof val === 'string') return val.trim();
    if (typeof val === 'object' && val !== null && 'main' in val) return String(val.main ?? '').trim();
    return '';
  };

  // Get alt string from an image object (handles alt_text, alt, altText, alt.main, description)
  const getImageAltStr = (img) => {
    if (!img) return '';
    const raw = img.alt_text ?? img.alt ?? img.altText ?? (img.alt && typeof img.alt === 'object' && 'main' in img.alt ? img.alt.main : null) ?? img.description ?? '';
    return getAltStr(raw);
  };

  // Image issue count: one per image missing alt — count ALL images, not just primary. Computed inline so badge always reflects current allImages.
  const list = Array.isArray(allImages) ? allImages : [];
  let imagesMissingAlt = 0;
  const imageDebug = []; // for debug log
  for (let i = 0; i < list.length; i++) {
    const img = list[i];
    if (!img) continue;
    const alt = img.alt_text ?? img.alt ?? img.altText ?? '';
    const str = typeof alt === 'string' ? alt.trim() : (alt && typeof alt === 'object' && 'main' in alt ? String(alt.main ?? '').trim() : '');
    const missing = !str;
    if (missing) imagesMissingAlt++;
    imageDebug.push({ i, id: img.id, alt_text: img.alt_text, alt: img.alt, altText: img.altText, str: str || '(empty)', missing });
  }
  const hasAnyImageItem = list.some((img) => !!(img && (img.url || img.imageUrl || img.originalUrl)));
  const computedImageScore = list.length > 0
    ? (hasAnyImageItem ? 0 : 1) + imagesMissingAlt
    : (getAltStr(primaryImageAltText) ? 0 : 1) + ((list.length > 0 || !!page?.imageUrl) ? 0 : 1);
  // Debug: image score (filter console by "[SEO Image Score]") — disabled to avoid log spam; re-enable if needed
  // if (typeof console !== 'undefined' && console.log && list.length > 0) {
  //   logger.log('[SEO Image Score]', { listLength: list.length, imagesMissingAlt, computedImageScore, perImage: imageDebug });
  // }

  // Image Issues Checklist (match backend: one issue per image missing alt)
  const hasAnyImage = allImages.length > 0 || !!page.imageUrl;
  const primaryImageHasAltText = getImageAltStr(allImages.find(img => img.is_primary) || allImages[0]) || getAltStr(primaryImageAltText) || '';

  const imageIssues = [
    { 
      label: t('seoOptimizerDetail.checklistImageAvailable'), 
      passed: hasAnyImage
    },
    { 
      label: t('seoOptimizerDetail.checklistAltTextPrimary'), 
      passed: !!primaryImageHasAltText
    },
    ...(allImages.length > 1 ? [{
      label: t('seoOptimizerDetail.checklistAllImagesAlt', { count: imagesMissingAlt }),
      passed: imagesMissingAlt === 0
    }] : [])
  ];
  
  // Calculate SEO scores dynamically (simplified - backend should provide these)
  // Use only primitive/stable deps to avoid infinite loop (urlCheckWords/linkValidation/initialSeoData are new refs every render)
  const linkInternal = linkValidation.internalBrokenLinks;
  const linkExternal = linkValidation.externalBrokenLinks;
  const linkHttp = linkValidation.httpLinks;
  const dupTitles = initialSeoData?.duplicateTitleTags ?? 0;
  const dupMeta = initialSeoData?.duplicateMetaDescriptions ?? 0;

  useEffect(() => {
    const list = effectiveKeywordStr ? effectiveKeywordStr.split(/[,\n]/).map(s => s.trim()).filter(s => s.length > 0) : [];
    const titleTagStr = titleTag ? String(titleTag) : '';
    const metaDescriptionStr = metaDescription ? String(metaDescription) : '';
    const descriptionStr = description ? String(description) : '';
    const descriptionPlainText = descriptionStr.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    const normalizedCustomUrl = customUrl ? customUrl.trim() : '';
    let customUrlPath = normalizedCustomUrl;
    try {
      const urlObj = new URL(normalizedCustomUrl.startsWith('http') ? normalizedCustomUrl : `https://example.com${normalizedCustomUrl}`);
      customUrlPath = urlObj.pathname;
    } catch (e) {
      customUrlPath = normalizedCustomUrl;
    }
    const customUrlPathNorm = customUrlPath.replace(/^\/+|\/+$/g, '');
    const customUrlKeywordHaystack = customUrlPathNorm.toLowerCase().replace(/[^a-z0-9-]/g, '');
    const words = new Set();
    list.forEach(phrase => {
      phrase.toLowerCase().split(/[\s-]+/).filter(w => w.length >= 2).forEach(w => {
        const c = w.replace(/[^a-z0-9]/g, '');
        if (c) words.add(c);
      });
    });
    (name || '').trim().toLowerCase().split(/[\s-]+/).filter(w => w.length >= 2).forEach(w => {
      const c = w.replace(/[^a-z0-9]/g, '');
      if (c) words.add(c);
    });
    const urlCheckWordsArr = [...words];
    const keywordInUrl = customUrlKeywordHaystack && urlCheckWordsArr.length > 0 && urlCheckWordsArr.some(word => customUrlKeywordHaystack.includes(word));

    let metaScore = 0;
    const titleCharCount = titleTagStr.length;
    const metaDescCharCount = metaDescriptionStr.length;
    if (titleCharCount < 40 || titleCharCount > 60) metaScore++;
    if (metaDescCharCount < 120 || metaDescCharCount > 160) metaScore++;
    const keywordInTitle = list.length > 0 && titleTagStr && list.some(kw => titleTagStr.toLowerCase().includes(kw.toLowerCase()));
    if (!keywordInTitle && list.length > 0) metaScore++;
    const keywordInMeta = list.length > 0 && metaDescriptionStr && list.some(kw => metaDescriptionStr.toLowerCase().includes(kw.toLowerCase()));
    if (!keywordInMeta && list.length > 0) metaScore++;
    if (dupTitles > 0) metaScore++;
    if (dupMeta > 0) metaScore++;

    let contentScore = 0;
    const keywordInDesc = list.length > 0 && descriptionStr && list.some(kw => descriptionPlainText.toLowerCase().includes(kw.toLowerCase()));
    if (!keywordInDesc && list.length > 0) contentScore++;
    if (descriptionPlainText.toLowerCase().includes('lorem ipsum')) contentScore++;
    contentScore += linkInternal;
    contentScore += linkExternal;
    contentScore += linkHttp;

    let imageScore = 0;
    const imgList = Array.isArray(allImages) ? allImages : [];
    const hasAnyImg = imgList.length > 0 || !!page?.imageUrl;
    if (imgList.length > 0) {
      const hasAnyImageItem = imgList.some((img) => !!(img && (img.url || img.imageUrl || img.originalUrl)));
      if (!hasAnyImageItem) imageScore++;
      imgList.forEach((img) => {
        if (!img) return;
        const alt = getAltStr(img.alt_text ?? img.alt ?? img.altText);
        if (!alt) imageScore++;
      });
    } else {
      if (!getAltStr(primaryImageAltText)) imageScore++;
      if (!hasAnyImg) imageScore++;
    }

    let urlScore = 0;
    if (urlCheckWordsArr.length > 0 && customUrlKeywordHaystack) {
      if (!keywordInUrl) urlScore++;
    } else {
      urlScore++;
    }

    const brokenScore = linkInternal + linkExternal;
    const overallScore = Math.max(0, Math.min(100, 100 - (metaScore * 5) - (contentScore * 3) - (imageScore * 5) - (brokenScore * 5) - (urlScore * 3)));

    setSeoScores({
      meta: metaScore,
      content: contentScore,
      image: imageScore,
      broken: brokenScore,
      url: urlScore
    });
    setSeoScore(overallScore);
  }, [titleTag, metaDescription, description, targetKeyword, name, customUrl, allImages, primaryImageAltText, linkInternal, linkExternal, linkHttp, dupTitles, dupMeta, effectiveKeywordStr, page?.imageUrl]);
  
  const handleSave = async (exitAfterSave = false, event) => {
    if (event?.currentTarget) saveButtonRef.current = event.currentTarget;
    if (!hashSiteId) {
      showToast(t('seoOptimizer.storeIdNotFound'), 'error', 3000, { anchor: saveButtonRef, anchorPosition: 'above' });
      return;
    }
    
    setSaving(true);
    setSavingAction(exitAfterSave ? 'saveAndExit' : 'save');
    setSaveError(null);
    setSaveSuccess(false);
    
    try {
      const response = await fetch(`${apiUrl}/api/seo-optimizer/save?id=${hashSiteId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: (() => {
          const payload = {
            pageId: page.pageId || page.id,
            pageType: page.pageType || page.type || 'product',
            meta_title: titleTag,
            meta_description: metaDescription,
            description: description,
            custom_url: customUrl,
            target_keyword: targetKeyword,
            page_name: page.pageName || page.name || name,
            page_url: page.urlLink || page.url || customUrl,
            url_link: page.urlLink || page.url || customUrl,
            scores: {
              meta: seoScores.meta,
              content: seoScores.content,
              image: computedImageScore,
              broken: seoScores.broken,
              url: seoScores.url,
              seoScore: seoScore
            },
            allImages: allImages.map(img => ({
              id: img.id,
              alt_text: img.alt_text || '',
              hasImage: true,
              url: img.url || img.imageUrl || img.originalUrl,
              width: img.width,
              height: img.height
            })),
            section_publish_to_ecwid: sectionPublishToEcwid,
          };
          // [DEBUG] Category alt: payload sent on Save (alt is persisted to Ecwid only on this action)
          if (page.pageType === 'category' || page.type === 'category') {
            logger.log('[CATEGORY_ALT] Save clicked. allImages being sent:', payload.allImages);
          }
          return JSON.stringify(payload);
        })()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save');
      }
      
      showToast(t('seoOptimizerDetail.savedSuccess'), 'success', 3000, { anchor: saveButtonRef, anchorPosition: 'above' });
      
      // Update initial values so "unsaved changes" is cleared
      setInitialValues({
        titleTag,
        metaDescription,
        description,
        customUrl,
        targetKeyword,
        allImages: allImages.map(img => ({ id: img.id, alt_text: img.alt_text || '' }))
      });
      
      // When "Save & Exit", close and return to list (no refetch on plain Save to avoid loading flash)
      if (exitAfterSave) {
        setTimeout(() => {
          if (onSave) onSave({ name, titleTag, metaDescription, description, customUrl, allImages });
          setTimeout(() => onClose(), 500);
        }, 500);
      }
    } catch (error) {
      logger.error('Error saving:', error);
      showToast(error.message || t('seoOptimizerDetail.failedToSave'), 'error', 3000, { anchor: saveButtonRef, anchorPosition: 'above' });
    } finally {
      setSaving(false);
      setSavingAction(null);
    }
  };
  
  const handleGenerateText = async (type, imageId, imageUrl, event) => {
    if (event?.currentTarget) aiFixAnchorRef.current = event.currentTarget;
    logger.log('=== handleGenerateText called ===', { type, imageId, imageUrl, hashSiteId, apiUrl });
    const loadingKey = (type === 'alt_text' && imageId !== undefined) ? `alt_text_${imageId}` : type;
    setAiLoading(prev => ({ ...prev, [loadingKey]: true }));
    setSaveError(null);
    
    try {
      // For alt_text with image URL use image/url API (same as products); otherwise use text generate
      const useImageAlt = type === 'alt_text' && imageUrl && imageUrl.trim().length > 0;
      const url = useImageAlt
        ? `${apiUrl}/api/seo-optimizer/generate-image-alt?id=${hashSiteId}`
        : `${apiUrl}/api/seo-optimizer/generate-text?id=${hashSiteId}`;
      logger.log('Calling API:', url, useImageAlt ? '(image alt)' : '(text)');
      const requestBody = useImageAlt
        ? {
            imageUrl: imageUrl.trim(),
            product_name: name || '',
            description: typeof description === 'string' ? description.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 400) : ''
          }
        : (() => {
            const body = {
              type,
              context: description || name,
              seo_keywords: effectiveKeyword ? effectiveKeyword.split(/[,\n]/).map(item => item.trim()).filter(item => item.length > 0) : (name ? [name] : []),
              product_name: name || '',
              productName: name || '',
              description: description || '',
              targetKeyword: effectiveKeyword || '',
              language: contentLanguage || 'en'
            };
            // Primary/thumbnail image URL (for AI context) - same shape as BigCommerce
            const thumbnailUrl = page?.imageUrl || allImages?.[0]?.thumbnail_url || allImages?.[0]?.url || allImages?.[0]?.imageUrl || '';
            if (thumbnailUrl) body.thumbnail_url = thumbnailUrl;
            // For description and keywords: pass additional_data including category names for AI context
            // Product: detailAdditionalData has category_name/parent_category_name from detail API; category: we add them here
            if (type === 'description' || type === 'keywords') {
              const ad = detailAdditionalData ?? page?.additional_data;
              let merged = ad && typeof ad === 'object' ? { ...ad } : {};
              if (page?.type === 'category' && (name || categoryInfo)) {
                if (name) merged.category_name = name;
                if (categoryInfo?.parentCategoryName != null && categoryInfo.parentCategoryName !== '') merged.parent_category_name = categoryInfo.parentCategoryName;
              }
              if (Object.keys(merged).length > 0) body.additional_data = merged;
            }
            return body;
          })();
      logger.log('Request body:', requestBody);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      logger.log('Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorData = await response.json();
        logger.log('=== Error Response ===');
        logger.log('Status:', response.status);
        logger.log('Error data:', errorData);
        
        // Check for quota/credit exhaustion – always show SweetAlert, never toast
        const isQuotaError = response.status === 403 ||
            errorData?.credits_exhausted ||
            errorData?.quota_exceeded ||
            errorData?.error === 'Insufficient credits' ||
            errorData?.error === 'Quota exceeded' ||
            (typeof errorData?.error === 'string' && (errorData.error.toLowerCase().includes('credit') || errorData.error.toLowerCase().includes('quota'))) ||
            (typeof errorData?.message === 'string' && (errorData.message.toLowerCase().includes('credit') || errorData.message.toLowerCase().includes('quota')));
        if (isQuotaError) {
          logger.log('Detected quota/credit exhaustion, showing toast');
          setAiLoading(prev => ({ ...prev, [loadingKey]: false }));
          const quotaMsg = `${t('seoOptimizerDetail.creditsExhausted')} ${t('dashboard.quotaExceededUpgrade')}`;
          showToast(quotaMsg, 'error', 6000, { anchor: aiFixAnchorRef, anchorPosition: 'above' });
          return;
        }
        
        logger.log('Throwing error:', errorData?.error || errorData?.message);
        throw new Error(errorData?.error || errorData?.message || 'Failed to generate text');
      }
      
      const data = await response.json();
      logger.log('=== AI Fix Response ===');
      logger.log('Response data:', data);
      logger.log('Response data keys:', Object.keys(data || {}));
      logger.log('data.success:', data.success);
      logger.log('data.seo_text:', data.seo_text);
      logger.log('data.alt_text:', data.alt_text);
      logger.log('data.generatedText:', data.generatedText);
      
      if (!data.success) {
        logger.error('Response indicates failure:', data);
        throw new Error(data?.error || data?.message || 'Failed to generate text');
      }
      
      // Try multiple possible response field names (generate-image-alt returns alt_text; generate-text returns seo_text)
      const seoText = data.alt_text || data.seo_text || data.generatedText || data.text || data.result || '';
      
      logger.log('Extracted seoText:', seoText);
      logger.log('seoText type:', typeof seoText);
      logger.log('seoText length:', seoText?.length);
      
      if (seoText && typeof seoText === 'string' && seoText.trim().length > 0) {
        logger.log('Setting text for type:', type);
        const trimmedText = seoText.trim();
        logger.log('Trimmed text:', trimmedText);
        
        if (type === 'title') {
          logger.log('Setting titleTag to:', trimmedText);
          setTitleTag(trimmedText);
        } else if (type === 'meta_description') {
          logger.log('Setting metaDescription to:', trimmedText);
          setMetaDescription(trimmedText);
        } else if (type === 'description') {
          logger.log('Setting description to:', trimmedText);
          setDescription(normalizeDescriptionForQuill(trimmedText));
        } else if (type === 'keywords') {
          // API returns comma-separated keywords; paste into target keyword field (same as BigCommerce)
          setTargetKeyword(trimmedText);
        } else if (type === 'alt_text') {
          // [DEBUG] Category/product image alt: only stored in state here; saved to Ecwid when user clicks Save
          logger.log('[CATEGORY_ALT] Alt text generated (in state only). Will be saved to Ecwid when you click Save.', { trimmedText: trimmedText.substring(0, 60), imageId });
          setAllImages(prev => prev.map((img, idx) => {
            const id = img.id ?? img.imageId ?? String(idx);
            if (imageId !== undefined && id !== imageId) return img;
            return { ...img, alt_text: trimmedText, alt: trimmedText };
          }));
          setPrimaryImageAltText(trimmedText);
        }
        logger.log('State update call completed for type:', type);
      } else {
        logger.error('No valid text found in response:', {
          alt_text: data.alt_text,
          seo_text: data.seo_text,
          generatedText: data.generatedText,
          text: data.text,
          result: data.result,
          fullData: data
        });
        throw new Error(t('seoOptimizerDetail.noTextGenerated'));
      }
    } catch (error) {
      logger.error('Error generating text:', error);
      logger.error('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      const msg = error.message || '';
      const isQuotaMessage = msg.toLowerCase().includes('quota') || msg.toLowerCase().includes('credit') || msg.toLowerCase().includes('insufficient');
      if (isQuotaMessage) {
        showToast(msg, 'error', 2000, { anchor: aiFixAnchorRef, anchorPosition: 'above' });
      } else {
        showToast(msg || t('seoOptimizerDetail.failedToGenerate'), 'error', 2000, { anchor: aiFixAnchorRef, anchorPosition: 'above' });
      }
    } finally {
      setAiLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };
  
  // Keywords available for "Add to Rank Tracking" dropdown: from target keyword (comma/newline split) or page name
  const addKeywordOptionList = effectiveKeywordList.length > 0
    ? effectiveKeywordList
    : (name && String(name).trim() ? [String(name).trim()] : []);
  const useKeywordDropdown = addKeywordOptionList.length > 0;

  const openAddKeywordModal = () => {
    setAddKeywordSelectedKeywords([]);
    setAddKeywordKeyword(useKeywordDropdown ? '' : ((targetKeyword && targetKeyword.trim()) ? targetKeyword.trim() : (name || page?.pageName || page?.name || '')));
    setAddKeywordCountry('United States');
    setAddKeywordDevice('Desktop');
    setAddKeywordEngine('google.com');
    setAddKeywordLanguage('English');
    setShowAddKeywordModal(true);
  };

  const handleAddKeywordSubmit = async () => {
    const toastOpts = { anchor: addKeywordSubmitRef, anchorPosition: 'above' };
    if (!hashSiteId) {
      showToast(t('seoOptimizerDetail.storeIdNotFound'), 'error', 2000, toastOpts);
      return;
    }
    const keywordsToAdd = useKeywordDropdown
      ? addKeywordSelectedKeywords.filter(k => (k || '').trim().length > 0)
      : [(addKeywordKeyword || '').trim()].filter(Boolean);
    if (keywordsToAdd.length === 0) {
      showToast(t('seoOptimizerDetail.keywordRequired'), 'error', 2000, toastOpts);
      return;
    }
    if (!useKeywordDropdown && keywordsToAdd[0].includes(',')) {
      showToast(t('seoOptimizerDetail.onlyOneKeywordAllowed'), 'error', 2000, toastOpts);
      return;
    }
    setAddKeywordSubmitting(true);
    try {
      let successCount = 0;
      let quotaExceeded = false;
      for (const keyword of keywordsToAdd) {
        const response = await fetch(`${apiUrl}/api/seo-optimizer/add-keyword?id=${hashSiteId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            keyword: keyword.trim(),
            country: addKeywordCountry,
            device: addKeywordDevice,
            engine: addKeywordEngine,
            language: addKeywordLanguage,
            pageId: page?.pageId || page?.id,
            pageType: page?.pageType || page?.type || 'product'
          })
        });
        const data = await response.json();
        if (!response.ok) {
          if (response.status === 403 && data.code === 'QUOTA_EXCEEDED') {
            quotaExceeded = true;
            const keywordBaseMsg = data.quota
              ? t('seoOptimizerDetail.keywordQuotaExceeded', { used: data.quota.used, limit: data.quota.limit })
              : t('seoOptimizerDetail.keywordQuotaExceededGeneric');
            const msg = `${keywordBaseMsg} ${t('dashboard.quotaExceededUpgrade')}`;
            showToast(msg, 'error', 6000, { anchor: addKeywordSubmitRef, anchorPosition: 'above' });
            break;
          }
          throw new Error(data.error || 'Failed to add keyword');
        }
        successCount++;
      }
      if (successCount > 0) {
        showToast(successCount === 1 ? t('seoOptimizerDetail.keywordAdded') : t('seoOptimizerDetail.keywordsAdded', { count: successCount }), 'success', 3000, { anchor: addKeywordSubmitRef, anchorPosition: 'above' });
        setShowAddKeywordModal(false);
      }
    } catch (err) {
      showToast(err.message || t('seoOptimizerDetail.failedToAddKeyword'), 'error', 2000, { anchor: addKeywordSubmitRef, anchorPosition: 'above' });
    } finally {
      setAddKeywordSubmitting(false);
    }
  };

  const toggleAddKeywordSelection = (kw) => {
    setAddKeywordSelectedKeywords(prev =>
      prev.includes(kw) ? prev.filter(k => k !== kw) : [...prev, kw]
    );
  };

  const startDetailTour = useCallback(() => {
    setDetailTourReplayFlyout(false);
    setDetailTourRun(true);
  }, []);
  const handleDetailTourClose = useCallback(() => {
    setDetailTourRun(false);
  }, []);
  const handleDetailTourExit = useCallback((reason) => {
    if (reason === 'finished' || reason === 'skipped') {
      setDetailTourReplayFlyout(true);
    }
  }, []);
  const handleReplayFlyoutFinished = useCallback(() => {
    setDetailTourReplayFlyout(false);
  }, []);

  const sectionEcwidSwitch = (sectionKey, idSuffix) => (
    <div
      className="form-check form-switch mb-0 ms-sm-auto flex-shrink-0"
      title={t('seoOptimizerDetail.publishSectionToEcwidHelp')}
    >
      <input
        className="form-check-input"
        type="checkbox"
        role="switch"
        id={`seo-detail-section-${idSuffix}`}
        checked={!!sectionPublishToEcwid[sectionKey]}
        onChange={(e) => { void updateSectionPublishToEcwidKey(sectionKey, e.target.checked); }}
      />
      <label className="form-check-label small text-nowrap text-muted ps-1 mb-0" htmlFor={`seo-detail-section-${idSuffix}`}>
        {t('seoOptimizerDetail.publishSectionToEcwid')}
      </label>
    </div>
  );

  // Get score color
  const getScoreColor = (score) => {
    if (score > 95) return 'success';
    if (score >= 80) return 'warning';
    return 'danger';
  };
  
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center seo-detail-loading-wrap">
        <div className="spinner-border spinner-border-sm text-primary" role="status">
          <span className="visually-hidden">{t('common.loading')}</span>
        </div>
      </div>
    );
  }
  
  const totalIssues = seoScores.meta + seoScores.content + computedImageScore + seoScores.url + seoScores.broken;
  
  return (
    <div className="seo-detail-root" style={{ backgroundColor: '#f8f9fa', paddingTop: '0' }}>
      {/* Sticky Navigation Header - on mobile sits below hamburger via CSS top offset */}
      <div 
        className="seo-detail-sticky-header"
        style={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          borderBottom: '1px solid #dee2e6',
          zIndex: 100,
          padding: '1rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}
      >
        <div id="seo-detail-nav-bar" className="d-flex justify-content-between align-items-center gap-3">
          {/* Left side: Back button and position */}
          <div className="d-flex align-items-center gap-2" style={{ flex: '0 0 auto' }}>
            <button 
              type="button"
              className="btn btn-link text-decoration-none p-0"
              onClick={handleClose}
              title={t('seoOptimizerDetail.backToOverview')}
            >
              <ArrowLeft size={16} className="me-1" />
              {t('seoOptimizerDetail.back')}
            </button>
            <span className="text-muted small">{currentPosition} of {totalRecords}</span>
          </div>

          {/* Middle: Navigation buttons */}
          <div className="d-flex align-items-center gap-2" style={{ flex: '0 0 auto' }}>
            <button 
              type="button"
              className="btn btn-light btn-sm" 
              onClick={() => setRefreshKey(prev => prev + 1)}
              disabled={loading}
              title={t('seoOptimizerDetail.refreshData')}
            >
              <RotateCcw size={14} className="me-1" style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
              {t('common.refresh')}
            </button>
            <button type="button" className="btn btn-light btn-sm" onClick={handlePrev} disabled={!hasPrev}>{t('seoOptimizerDetail.previous')}</button>
            <button type="button" className="btn btn-light btn-sm" onClick={handleNext} disabled={!hasNext}>
              {t('seoOptimizerDetail.next')} <span className="ms-1">›</span>
            </button>
          </div>

          {/* Right side: Page tour + Save */}
          <div className="d-flex align-items-center gap-2" style={{ flex: '0 0 auto' }}>
            <button
              type="button"
              id="seo-detail-page-tour-button"
              className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
              onClick={startDetailTour}
              title={t('seoFixDetailTour.welcomeTitle')}
            >
              <Route size={14} aria-hidden />
              {t('seoFixDetailTour.pageTourButton')}
            </button>
            <button 
              ref={saveButtonRef}
              className="btn btn-outline-secondary btn-sm"
              onClick={(e) => handleSave(false, e)} 
              disabled={saving}
              title={t('seoOptimizerDetail.saveChanges')}
            >
              {saving && savingAction === 'save' ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  {t('common.saving')}
                </>
              ) : (
                <>
                  <Check size={14} className="me-1" />
                  {t('common.save')}
                </>
              )}
            </button>
            <button 
              type="button"
              className="btn btn-success btn-sm"
              onClick={(e) => handleSave(true, e)} 
              disabled={saving}
              title={t('seoOptimizerDetail.saveAndReturn')}
            >
              {saving && savingAction === 'saveAndExit' ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  {t('common.saving')}
                </>
              ) : (
                <>
                  <Check size={14} className="me-1" />
                  {t('seoOptimizerDetail.saveAndReturn')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="seo-detail-content" style={{ padding: '1rem' }}>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>

        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
          <h5 className="mb-0 fw-bold flex-grow-1 min-w-0">{name || page.pageName || t('seoOptimizerDetail.untitledProduct')}</h5>
          <div
            className="seo-detail-content-language d-flex align-items-center gap-2 flex-shrink-0 rounded px-3 py-2 border"
            style={{
              backgroundColor: 'var(--app-primary-bg-subtle, rgba(12, 44, 85, 0.08))',
              borderColor: 'var(--app-primary-border-subtle, rgba(12, 44, 85, 0.25))',
            }}
            title={t('seoOptimizerDetail.contentLanguageTooltip')}
          >
            <Globe size={18} style={{ color: 'var(--app-primary, #0c2d55)' }} aria-hidden />
            <label className="form-label mb-0 me-1 fw-semibold text-dark" style={{ fontSize: '0.95rem', whiteSpace: 'nowrap' }}>{t('seoOptimizerDetail.contentLanguage')}</label>
            <select
              ref={contentLanguageRef}
              id="detail-content-language"
              className="form-select form-select-sm fw-medium seo-detail-lang-select"
              style={{ width: 'auto', minWidth: '9rem', fontSize: '0.95rem' }}
              value={contentLanguage}
              onChange={async (e) => {
                const value = e.target.value;
                setContentLanguage(value);
                const toastOpts = { anchor: contentLanguageRef, anchorPosition: 'above' };
                try {
                  const res = await fetch(`${apiUrl}/api/seo-optimizer/settings?id=${hashSiteId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ content_language: value })
                  });
                  if (!res.ok) throw new Error('Failed to save');
                  showToast(t('seoOptimizerDetail.contentLanguageSaved'), 'success', 3000, toastOpts);
                } catch (err) {
                  logger.error('Error saving content language:', err);
                  showToast(t('seoOptimizerDetail.failedToSaveContentLanguage'), 'error', 2000, toastOpts);
                }
              }}
            >
              {CONTENT_LANGUAGE_OPTIONS.map((opt) => (
                <option key={opt.code} value={opt.code}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div id="seo-detail-score-row" className="d-flex flex-wrap gap-2 mb-3 align-items-center">
          <span className="badge bg-light text-dark border" style={{ fontSize: '1.2rem', padding: '0.5rem 0.75rem', fontWeight: 'bold' }}>SEO Score: {seoScore}%</span>
          <span className="badge bg-light text-danger border">Issues: {totalIssues}</span>
          <span className="badge bg-light text-dark border">Meta: {seoScores.meta}</span>
          <span className="badge bg-light text-dark border">Images: {computedImageScore}</span>
        </div>

        {/* Product / Category Name */}
        {/* <div className="card mb-3 border-0 shadow-sm">
          <div className="card-body py-2">
            <label className="form-label small fw-bold mb-1">
              {(page?.pageType === 'category' || page?.type === 'category') ? t('seoOptimizerDetail.categoryName') : t('seoOptimizerDetail.productName')}
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {categoryInfo && (
              <div className="d-flex flex-wrap gap-2 align-items-center small text-muted mt-1">
                {categoryInfo.parentCategoryName != null && categoryInfo.parentCategoryName !== '' && (
                  <span>Parent: {categoryInfo.parentCategoryName}</span>
                )}
                {categoryInfo.productCount != null && (
                  <span>Products: {categoryInfo.productCount}</span>
                )}
                {categoryInfo.enabled === false && (
                  <span className="text-warning">Disabled</span>
                )}
              </div>
            )}
          </div>
        </div> */}

        {/* Target keyword */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-header bg-white border-0 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="fw-semibold">{t('seoOptimizerDetail.targetKeywordLabel')}</div>
            {sectionEcwidSwitch('targetKeyword', 'target-keyword')}
          </div>
          <div className="card-body">
            <div id="seo-detail-keywords-intro" className="mb-2">
              <input
                type="text"
                className="form-control form-control-sm"
                value={targetKeyword}
                onChange={(e) => setTargetKeyword(e.target.value)}
                placeholder={t('seoOptimizerDetail.targetKeywordPlaceholder')}
              />
              <p className="text-muted small mt-2 mb-0">{t('seoFixDetailTour.keywordsFieldHint')}</p>
            </div>
            <div className="d-flex gap-2 align-items-start flex-wrap">
              <button
                id="seo-detail-keywords-ai-fix"
                type="button"
                className="btn btn-sm d-flex align-items-center gap-1 flex-shrink-0"
                style={violetAiBtnStyle}
                onClick={(e) => handleGenerateText('keywords', undefined, undefined, e)}
                disabled={aiLoading.keywords}
                title="AI Fix: generate SEO keywords"
              >
                {aiLoading.keywords ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                ) : (
                  <Wand2 size={16} />
                )}
                <span className="small">{t('seoOptimizerDetail.aiFix')}</span>
              </button>
              <button
                id="seo-detail-keywords-rank-track"
                type="button"
                className="btn btn-sm d-flex align-items-center gap-1 flex-shrink-0 border"
                style={skyRankBtnStyle}
                onClick={openAddKeywordModal}
                title={t('seoOptimizerDetail.addKeywordToRankTracker')}
              >
                <Plus size={16} />
                {t('seoOptimizerDetail.addForRankTracking')}
              </button>
            </div>
          </div>
        </div>

        {/* Titles & Meta */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-header bg-white border-0 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="fw-semibold">{t('seoOptimizerDetail.titlesAndMeta')}</div>
              <span className={`badge ${seoScores.meta === 0 ? 'bg-success' : 'bg-danger'}`}>{seoScores.meta}</span>
            </div>
            {sectionEcwidSwitch('meta', 'meta')}
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div id="seo-detail-titles-meta-rules" className="col-md-5">
                <ul className="list-unstyled mb-0" style={{ fontSize: '0.85rem' }}>
                  {metaTagIssues.map((item, index) => (
                    <li key={index} className="d-flex align-items-start mb-2">
                      <div className="me-2 mt-1">
                        {item.passed ? (
                          <Check size={12} className="text-success" />
                        ) : (
                          <X size={12} className="text-danger" />
                        )}
                      </div>
                      <div className="flex-grow-1">
                        <div>{item.label}</div>
                        {item.value !== undefined && (
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {t('seoOptimizerDetail.charsCount', { count: item.value })}
                          </div>
                        )}
                        {item.count !== undefined && item.count > 0 && (
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {t('seoOptimizerDetail.foundCount', { count: item.count })}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div id="seo-detail-titles-meta-edits" className="col-md-7">
                <div className="mb-3">
                  <label className="form-label small fw-bold mb-1">{t('seoOptimizerDetail.productTitle')}</label>
                  <div className="d-flex gap-2">
                    <div className="flex-grow-1 position-relative">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={titleTag}
                        onChange={(e) => setTitleTag(e.target.value)}
                      />
                      <div className="position-absolute top-0 end-0 p-1">
                        <span className={`small ${titleCharCount >= 40 && titleCharCount <= 60 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.7rem' }}>
                          {titleCharCount}
                        </span>
                      </div>
                    </div>
                    <button 
                      className="btn btn-sm d-flex align-items-center gap-1"
                      style={violetAiBtnStyle}
                      onClick={(e) => handleGenerateText('title', undefined, undefined, e)}
                      disabled={aiLoading.title}
                      title="AI-Assisted Fix"
                    >
                      {aiLoading.title ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          <span className="small">AI...</span>
                        </>
                      ) : (
                        <>
                          <Wand2 size={12} />
                          <span className="small">{t('seoOptimizerDetail.aiFix')}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <label className="form-label small fw-bold mb-0">{t('seoOptimizerDetail.metaDescription')}</label>
                    <div className="d-flex align-items-center gap-2">
                      <span className={`small ${metaDescCharCount >= 120 && metaDescCharCount <= 160 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.7rem' }}>
                        {metaDescCharCount}
                      </span>
                      <button
                        className="btn btn-sm d-flex align-items-center gap-1"
                        style={violetAiBtnStyle}
                        onClick={(e) => handleGenerateText('meta_description', undefined, undefined, e)}
                        disabled={aiLoading.meta_description}
                        title="AI-Assisted Fix"
                      >
                        {aiLoading.meta_description ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            <span className="small">AI...</span>
                          </>
                        ) : (
                          <>
                            <Wand2 size={12} />
                            <span className="small">{t('seoOptimizerDetail.aiFix')}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  <textarea
                    className="form-control form-control-sm"
                    rows={3}
                    value={metaDescription}
                    onChange={(e) => setMetaDescription(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Quality */}
        <div id="seo-detail-content-section" className="card border-0 shadow-sm mb-3">
          <div className="card-header bg-white border-0 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="fw-semibold">{t('seoOptimizerDetail.productDescription')}</div>
              <span className={`badge ${seoScores.content === 0 ? 'bg-success' : 'bg-danger'}`}>{seoScores.content}</span>
            </div>
            {sectionEcwidSwitch('content', 'content')}
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-5">
                <ul className="list-unstyled mb-0" style={{ fontSize: '0.85rem' }}>
                  {contentIssues.map((item, index) => (
                    <li key={index} className="d-flex align-items-start mb-2">
                      <div className="me-2 mt-1">
                        {item.passed ? (
                          <Check size={12} className="text-success" />
                        ) : (
                          <X size={12} className="text-danger" />
                        )}
                      </div>
                      <div className="flex-grow-1">
                        <div>{item.label}</div>
                        {item.count !== undefined && item.count > 0 && (
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            Found: {item.count}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="col-md-7">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label className="form-label small fw-bold mb-0">{t('seoOptimizerDetail.description')}</label>
                  <div className="d-flex align-items-center gap-2">
                    <span className="small text-muted" style={{ fontSize: '0.7rem' }}>
                      {descriptionCharCount} chars / {descriptionWordCount} words
                    </span>
                    <button
                      className="btn btn-sm d-flex align-items-center gap-1"
                      style={violetAiBtnStyle}
                      onClick={(e) => handleGenerateText('description', undefined, undefined, e)}
                      disabled={aiLoading.description}
                      title="AI-Assisted Fix"
                    >
                      {aiLoading.description ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          <span className="small">AI...</span>
                        </>
                      ) : (
                        <>
                          <Wand2 size={12} />
                          <span className="small">{t('seoOptimizerDetail.aiFix')}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <div ref={descriptionWrapperRef} style={{ border: '1px solid #dee2e6', borderRadius: '0.25rem' }}>
                  <ReactQuill
                    theme="snow"
                    value={description}
                    onChange={(content) => setDescription(content)}
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        [{ 'align': [] }],
                        ['link'],
                        ['clean']
                      ]
                    }}
                    formats={[
                      'header',
                      'bold', 'italic', 'underline', 'strike',
                      'list', 'bullet',
                      'align',
                      'link'
                    ]}
                    style={{ backgroundColor: 'white' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Images (products: multiple images + Generate Alt; categories: single image if set) */}
        <div id="seo-detail-images-section" className="card border-0 shadow-sm mb-3">
          <div className="card-header bg-white border-0 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="fw-semibold">{t('seoOptimizerDetail.images')}</div>
            <div className="d-flex align-items-center gap-2">
              {/* Generate Alt Text for All (product) - commented out
              {(page.pageType === 'product' || page.type === 'product') ? (
                <button 
                  ref={altTextAllButtonRef}
                  className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                  onClick={async (e) => {
                    altTextAllButtonRef.current = e?.currentTarget ?? null;
                    if (!hashSiteId || !page.pageId && !page.id) return;
                    setAiLoading(prev => ({ ...prev, alt_text_all: true }));
                    setSaveError(null);
                    const toastOpts = { anchor: altTextAllButtonRef, anchorPosition: 'above' };
                    showToast(t('seoOptimizerDetail.altTextGeneratingSnack'), 'info', 3000, toastOpts);
                    
                    try {
                      // Use bulk API to generate alt text for all images in this product
                      const productId = page.pageId || page.id;
                      const selectedProductObjects = [{
                        id: productId,
                        name: name || 'Product',
                        brand_id: 0 // Default brand ID for Ecwid
                      }];
                      
                      const response = await fetch(`${apiUrl}/api/bulkproduct`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          storeId: hashSiteId,
                          selectedProductObjects: selectedProductObjects
                        })
                      });
                      
                      const data = await response.json();
                      
                      if (!response.ok) {
                        throw new Error(data.error || data.message || 'Failed to generate alt text');
                      }
                      
                      showToast(t('seoOptimizerDetail.altTextGeneratedSuccess'), 'success', 3000, { anchor: altTextAllButtonRef, anchorPosition: 'above' });
                      // Refresh images after a short delay
                      setTimeout(() => {
                        fetch(
                          `${apiUrl}/api/seo-optimizer/detail?id=${hashSiteId}&pageId=${productId}&pageType=${page.pageType || page.type || 'product'}`
                        ).then(res => res.ok ? res.json() : null).then(data => {
                          if (data) {
                            setAllImages(data.all_images || data.images || []);
                            setPrimaryImageAltText(data.primary_image_alt_text || '');
                          }
                        }).catch(err => logger.error('Failed to refresh images:', err));
                      }, 500);
                    } catch (error) {
                      logger.error('Error generating alt text:', error);
                      showToast(error.message || t('seoOptimizerDetail.failedToGenerateAltText'), 'error', 3000, { anchor: altTextAllButtonRef, anchorPosition: 'above' });
                    } finally {
                      setAiLoading(prev => ({ ...prev, alt_text_all: false }));
                    }
                  }}
                  disabled={aiLoading.alt_text_all || !hashSiteId || (!page.pageId && !page.id)}
                  title={t('seoOptimizerDetail.generateAltTextForAllTitle')}
                >
                  {aiLoading.alt_text_all ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      <span className="small">{t('seoOptimizerDetail.generating')}</span>
                    </>
                  ) : (
                    <>
                      <Wand2 size={12} />
                      <span className="small">{t('seoOptimizerDetail.generateAltTextForAll')}</span>
                    </>
                  )}
                </button>
              ) : (
              */}
              {(page.pageType === 'product' || page.type === 'product') ? null : (
                <button 
                  className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                  onClick={(e) => handleGenerateText('alt_text', undefined, allImages[0]?.url || allImages[0]?.imageUrl || '', e)}
                  disabled={aiLoading.alt_text || !hashSiteId || allImages.length === 0}
                  title={t('seoOptimizerDetail.generateAltTextCategoryTitle')}
                >
                  {aiLoading.alt_text ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      <span className="small">{t('seoOptimizerDetail.generating')}</span>
                    </>
                  ) : (
                    <>
                      <Wand2 size={12} />
                      <span className="small">{t('seoOptimizerDetail.generateAltText')}</span>
                    </>
                  )}
                </button>
              )}
                <button 
                  className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                  onClick={async () => {
                    if (!hashSiteId) return;
                    setRefreshingImages(true);
                    try {
                      const response = await fetch(
                        `${apiUrl}/api/seo-optimizer/detail?id=${hashSiteId}&pageId=${page.pageId || page.id}&pageType=${page.pageType || page.type || 'product'}`
                      );
                      if (response.ok) {
                        const data = await response.json();
                        setAllImages(data.all_images || data.images || []);
                        setPrimaryImageAltText(data.primary_image_alt_text || '');
                      }
                    } catch (error) {
                      logger.error('Failed to refresh images:', error);
                    } finally {
                      setRefreshingImages(false);
                    }
                  }}
                  disabled={refreshingImages || !hashSiteId}
                  title={t('seoOptimizerDetail.refreshAllTitle')}
                >
                  {refreshingImages ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      <span className="small">{t('seoOptimizerDetail.refreshing')}</span>
                    </>
                  ) : (
                    <>
                      <RotateCcw size={12} />
                      <span className="small">{t('seoOptimizerDetail.refreshAll')}</span>
                    </>
                  )}
                </button>
                <span className={`badge ${computedImageScore === 0 ? 'bg-success' : 'bg-danger'}`}>{computedImageScore}</span>
              </div>
            </div>
            {sectionEcwidSwitch('images', 'images')}
          </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-5">
                  <ul className="list-unstyled mb-0" style={{ fontSize: '0.85rem' }}>
                    {imageIssues.map((item, index) => (
                      <li key={index} className="d-flex align-items-start mb-2">
                        <div className="me-2 mt-1">
                          {item.passed ? (
                            <Check size={12} className="text-success" />
                          ) : (
                            <X size={12} className="text-danger" />
                          )}
                        </div>
                        <div className="flex-grow-1">
                          <div>{item.label}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="col-md-7">
                  {allImages && allImages.length > 0 ? (
                    <div className="list-group list-group-flush">
                      {allImages.map((image, idx) => {
                        const imageId = image.id || image.imageId || idx;
                        const imageUrl = image.url || image.imageUrl || image.originalUrl || '';
                        const thumbnailUrl = image.thumbnail_url || image.thumbnailUrl || image.thumbnails?.small || imageUrl;
                        const altText = getImageAltStr(image);
                        const filename = image.filename || image.name || `image-${imageId}`;
                        
                        return (
                          <div key={imageId} className="list-group-item px-0 py-2 border-bottom">
                            <div className="d-flex align-items-start gap-2">
                              <div className="flex-shrink-0">
                                <div className="position-relative">
                                  {thumbnailUrl ? (
                                    <img 
                                      src={thumbnailUrl} 
                                      alt={altText || filename}
                                      className="rounded"
                                      style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                      onError={(e) => {
                                        // Fallback to original URL if thumbnail fails
                                        if (e.target.src !== imageUrl && imageUrl) {
                                          e.target.src = imageUrl;
                                        }
                                      }}
                                    />
                                  ) : (
                                    <div className="rounded bg-light d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                      <span className="small text-muted">{t('seoOptimizerDetail.noImg')}</span>
                                    </div>
                                  )}
                                  {image.is_primary && (
                                    <div
                                      className="position-absolute bg-warning rounded-circle d-flex align-items-center justify-content-center"
                                      style={{
                                        top: '-4px',
                                        right: '-4px',
                                        width: '20px',
                                        height: '20px',
                                      }}
                                    >
                                      <Star size={10} className="text-white" fill="currentColor" />
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <span className="small text-muted" style={{ fontSize: '0.75rem' }}>{filename}</span>
                                  {imageUrl && (
                                    <a 
                                      href={imageUrl} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-decoration-none"
                                    >
                                      <ExternalLink size={8} className="text-muted" />
                                    </a>
                                  )}
                                  {image.file_size && (
                                    <span className="small text-muted ms-auto" style={{ fontSize: '0.7rem' }}>{image.file_size}</span>
                                  )}
                                </div>
                                <div className="input-group input-group-sm">
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder={t('seoOptimizerDetail.altTextPlaceholder')}
                                    value={altText}
                                    onChange={(e) => {
                                      const updated = allImages.map(img => {
                                        const imgId = img.id || img.imageId || allImages.indexOf(img);
                                        return imgId === imageId 
                                          ? { ...img, alt_text: e.target.value, alt: e.target.value }
                                          : img;
                                      });
                                      setAllImages(updated);
                                    }}
                                    style={{ fontSize: '0.8rem' }}
                                  />
                                  <button 
                                    className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1" 
                                    type="button"
                                    onClick={(e) => handleGenerateText('alt_text', imageId, imageUrl || image.imageUrl || image.originalUrl || '', e)}
                                    disabled={aiLoading[`alt_text_${imageId}`] || aiLoading.alt_text || !hashSiteId || !(imageUrl || image.imageUrl || image.originalUrl)}
                                    title={t('seoOptimizerDetail.generateAltTextImageTitle')}
                                  >
                                    {aiLoading[`alt_text_${imageId}`] || aiLoading.alt_text ? (
                                      <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        <span className="small">AI...</span>
                                      </>
                                    ) : (
                                      <>
                                        <Wand2 size={12} />
                                        <span className="small">{t('seoOptimizerDetail.aiFix')}</span>
                                      </>
                                    )}
                                  </button>
                                  <button 
                                    className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 ms-2" 
                                    type="button"
                                    onClick={async () => {
                                      try {
                                        const response = await fetch(
                                          `${apiUrl}/api/seo-optimizer/detail?id=${hashSiteId}&pageId=${page.pageId || page.id}&pageType=${page.pageType || page.type || 'product'}`
                                        );
                                        if (response.ok) {
                                          const data = await response.json();
                                          const imagesData = data.all_images || data.images || [];
                                          const freshImage = imagesData.find(img => (img.id || img.imageId) === imageId);
                                          if (freshImage) {
                                            setAllImages(prev => prev.map(img => {
                                              const imgId = img.id || img.imageId || prev.indexOf(img);
                                              return imgId === imageId ? freshImage : img;
                                            }));
                                          }
                                        }
                                      } catch (error) {
                                        logger.error('Failed to refresh image:', error);
                                      }
                                    }}
                                    title={t('seoOptimizerDetail.refreshImageTitle')}
                                  >
                                    <RotateCcw size={12} />
                                    <span className="small">{t('seoOptimizerDetail.refresh')}</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-muted mb-0 small">
                      <p>{t('seoOptimizerDetail.noImagesFound')}</p>
                      {process.env.NODE_ENV === 'development' && (
                        <details className="mt-2">
                          <summary className="small">Debug Info</summary>
                          <pre className="small mt-2" style={{ fontSize: '0.7rem', maxHeight: '200px', overflow: 'auto' }}>
                            {JSON.stringify({ allImages, imagesLength: allImages?.length, imagesType: typeof allImages }, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

        {/* URL */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-header bg-white border-0 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="fw-semibold">{t('seoOptimizerDetail.url')}</div>
              <span className={`badge ${seoScores.url === 0 ? 'bg-success' : 'bg-danger'}`}>{seoScores.url}</span>
            </div>
            {sectionEcwidSwitch('url', 'url')}
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-5">
                <ul className="list-unstyled mb-0" style={{ fontSize: '0.85rem' }}>
                  <li className="d-flex align-items-start mb-2">
                    <div className="me-2 mt-1">
                      {(() => {
                        if (urlCheckWords.length === 0 || !customUrlKeywordHaystack) {
                          return <X size={12} className="text-danger" />;
                        }
                        return keywordInUrl ? (
                          <Check size={12} className="text-success" />
                        ) : (
                          <X size={12} className="text-danger" />
                        );
                      })()}
                    </div>
                    <div className="flex-grow-1">
                      <div>{t('seoOptimizerDetail.targetKeywordInUrl')}</div>
                    </div>
                  </li>
                </ul>
              </div>
              <div className="col-md-7">
                <label className="form-label small fw-bold mb-1">{t('seoOptimizerDetail.customUrl')}</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                  placeholder={t('seoOptimizerDetail.customUrlPlaceholder')}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Keyword to Rank Tracker Modal */}
      {showAddKeywordModal && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
          onClick={() => setShowAddKeywordModal(false)}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header border-bottom">
                <h5 className="modal-title fw-bold">{t('seoOptimizerDetail.addKeywordModalTitle')}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAddKeywordModal(false)}
                  aria-label={t('common.close')}
                />
              </div>
              <div className="modal-body">
                {useKeywordDropdown ? (
                  <div className="mb-3">
                    <label className="form-label small fw-bold">{t('seoOptimizerDetail.selectKeywords')}</label>
                    <p className="small text-muted mb-2">{t('seoOptimizerDetail.selectKeywordsHint')}</p>
                    <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {addKeywordOptionList.map((kw) => (
                        <div key={kw} className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`add-kw-${kw}`}
                            checked={addKeywordSelectedKeywords.includes(kw)}
                            onChange={() => toggleAddKeywordSelection(kw)}
                          />
                          <label className="form-check-label small" htmlFor={`add-kw-${kw}`}>
                            {kw}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mb-3">
                    <label className="form-label small fw-bold">{t('seoOptimizerDetail.enterKeyword')}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={addKeywordKeyword}
                      onChange={(e) => setAddKeywordKeyword(e.target.value)}
                      placeholder={t('seoOptimizerDetail.enterKeywordPlaceholder')}
                    />
                  </div>
                )}
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small fw-bold">{t('seoOptimizerDetail.country')}</label>
                    <select
                      className="form-select form-select-sm"
                      value={addKeywordCountry}
                      onChange={(e) => setAddKeywordCountry(e.target.value)}
                    >
                      <option value="United States">United States</option>
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="Canada">Canada</option>
                      <option value="Australia">Australia</option>
                      <option value="Germany">Germany</option>
                      <option value="France">France</option>
                      <option value="Spain">Spain</option>
                      <option value="Italy">Italy</option>
                      <option value="Netherlands">Netherlands</option>
                      <option value="India">India</option>
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label small fw-bold">{t('seoOptimizerDetail.device')}</label>
                    <select
                      className="form-select form-select-sm"
                      value={addKeywordDevice}
                      onChange={(e) => setAddKeywordDevice(e.target.value)}
                    >
                      <option value="Desktop">Desktop</option>
                      <option value="Mobile">Mobile</option>
                    </select>
                  </div>
                </div>
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small fw-bold">{t('seoOptimizerDetail.searchEngine')}</label>
                    <select
                      className="form-select form-select-sm"
                      value={addKeywordEngine}
                      onChange={(e) => setAddKeywordEngine(e.target.value)}
                    >
                      <option value="google.com">google.com</option>
                      <option value="google.co.uk">google.co.uk</option>
                      <option value="google.ca">google.ca</option>
                      <option value="google.com.au">google.com.au</option>
                      <option value="google.de">google.de</option>
                      <option value="google.fr">google.fr</option>
                      <option value="google.es">google.es</option>
                      <option value="bing.com">bing.com</option>
                      <option value="duckduckgo.com">duckduckgo.com</option>
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label small fw-bold">{t('seoOptimizerDetail.language')}</label>
                    <select
                      className="form-select form-select-sm"
                      value={addKeywordLanguage}
                      onChange={(e) => setAddKeywordLanguage(e.target.value)}
                    >
                      <option value="English">English</option>
                      <option value="Spanish">Spanish</option>
                      <option value="French">French</option>
                      <option value="German">German</option>
                      <option value="Italian">Italian</option>
                      <option value="Portuguese">Portuguese</option>
                      <option value="Dutch">Dutch</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-top">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddKeywordModal(false)}
                >
                  {t('common.cancel')}
                </button>
                <button
                  ref={addKeywordSubmitRef}
                  type="button"
                  className="btn btn-dark"
                  onClick={handleAddKeywordSubmit}
                  disabled={addKeywordSubmitting || (useKeywordDropdown ? addKeywordSelectedKeywords.length === 0 : !(addKeywordKeyword || '').trim())}
                >
                  {addKeywordSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                      {t('common.saving')}
                    </>
                  ) : (
                    t('seoOptimizerDetail.addKeyword')
                )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <SeoFixDetailGuidedTour
        run={detailTourRun}
        onClose={handleDetailTourClose}
        onExit={handleDetailTourExit}
        includeImagesStep
      />
      {detailTourReplayFlyout && (
        <SeoFixDetailTourReplayHintFlyout onFinished={handleReplayFlyoutFinished} />
      )}

      {/* Unsaved Changes Confirmation Modal - portaled to body so it stays viewport-centered */}
      {showUnsavedChangesModal && createPortal(
        <div 
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby="unsaved-changes-modal-title"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
          }}
          onClick={() => {
            setShowUnsavedChangesModal(false);
            setPendingNavigation(null);
          }}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header border-bottom">
                <h5 id="unsaved-changes-modal-title" className="modal-title fw-bold">{t('seoOptimizerDetail.unsavedChanges')}</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setShowUnsavedChangesModal(false);
                    setPendingNavigation(null);
                  }}
                  aria-label={t('common.close')}
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-0">{t('seoOptimizerDetail.unsavedChangesMessage')}</p>
              </div>
              <div className="modal-footer border-top">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowUnsavedChangesModal(false);
                    setPendingNavigation(null);
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button 
                  type="button" 
                  className="btn btn-outline-danger" 
                  onClick={() => {
                    setShowUnsavedChangesModal(false);
                    if (pendingNavigation) {
                      pendingNavigation();
                      setPendingNavigation(null);
                    }
                  }}
                >
                  {t('seoOptimizerDetail.discardAndContinue')}
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={async () => {
                    setShowUnsavedChangesModal(false);
                    await handleSave(false);
                    if (pendingNavigation) {
                      setTimeout(() => {
                        pendingNavigation();
                        setPendingNavigation(null);
                      }, 500);
                    }
                  }}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {t('common.saving')}
                    </>
                  ) : (
                    t('seoOptimizerDetail.saveAndContinue')
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SEOOptimizerDetail;
